import { useAppDispatch, useAppSelector } from 'appState/store'
import NoResultsAfterFilters from 'assets/images/table/NoResultsAfterFilters.svg?react'
import { AxiosError } from 'axios'
import { BreadcrumbHeader } from 'components/BreadcrumbSection/BreadcrumbHeader'
import { ErrorBox } from '@outbound-ai/ursa'
import { LoadingBox } from '@outbound-ai/ursa'
import { apiPaths, RefreshIntervalsEnum } from 'configs/apiConfigs'
import { startCall } from 'features/calls/callsApi'
import { useCall, useCurrentUserCalls } from 'features/calls/callsHooks'
import { updateLiveCall } from 'features/calls/callsSlice'
import { AutomationTypeEnum, CallStatusEnum, CallTypeEnum, ICall, UseCaseEnum } from 'features/calls/types'
import { AssignAndUnAssign } from 'features/claims'
import { archiveClaim, getPayerAvailability } from 'features/claims/claimsApi'
import { useClaim, useLevelofSupport } from 'features/claims/claimsHooks'
import {
  ClaimCallJobStandardModal,
  ClaimCallJobStartedModal,
  ClaimCallMenu,
  ClaimDetails,
} from 'features/claims/components'
import { PayerCallRequestConfirmationModal } from 'features/claims/components/ClaimDetails/PayerCallRequestConfirmationModal'
import { isCurrentUserClaimOwner } from 'features/claims/utils'
import { getClaimsPath } from 'features/claims/utils/claimsPathUtils'
import { getUnblockMicHelperUrl } from 'features/claims/utils/misc'
import { useEntities } from 'features/entities/entityHooks'
import { useUserPreference, useUserProfile } from 'features/settings/settingsHooks'
import { DEFAULT_PAYER_VA_SOFTPHONE_KEY, UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { useMedia } from 'hooks/useMedia'
import { useRevalidateAllSwrCache } from 'hooks/useRevalidateAllSwrCache'
import { PathsEnum } from 'pages/Paths'
import { createElement, useEffect, useRef, useState } from 'react'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { retrieveTenantId, isManualTenantSwitch } from 'utils/tenantUtils'
import { Breadcrumb } from 'react-bootstrap'
import { renderToStaticMarkup } from 'react-dom/server'
import { FaCaretRight, FaCheckSquare, FaRegCheckSquare } from 'react-icons/fa'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { formatPhoneNumber, sendLogs } from 'utils'
import { getBrowser } from 'utils/device'
import { errorHandler } from 'utils/errors/errors'
import { notificator } from 'utils/notificator/notificator'

import { mapEntities } from 'utils/dataExtractor'
import { LiveCallActionDisplay } from './internal/LiveCallActionDisplay'
import { StartManualCallButton } from './internal/StartManualCallButton'

// eslint-disable-next-line import/named
export const ClaimDetailsPage = () => {
  /**
   * Hooks
   */
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { activeCallJobId } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const assistedCallEnabled = profile?.assistedCallEnabled

  const { oaiClaimId, tenantId: tenantIdFromParams } = useParams<{ oaiClaimId: string; tenantId: string }>()
  const [slotData, setSlotData] = useState<any>(null)
  const { microphonePermissionStatus } = useMedia()
  const hasTriedTenantRetrieval = useRef(false)
  const [isRetrievingTenant, setIsRetrievingTenant] = useState(false)

  // Get tenant from URL params or fallback to helper (for old URL compatibility)
  const tenantId = tenantIdFromParams || getTntIdForNavigation()

  /**
   * Payer Availability
   */
  const [showPayerCallRequestModal, setShowPayerCallRequestModal] = useState(false)

  /**
   * SWR
   */
  const revalidateAllSwrCache = useRevalidateAllSwrCache()

  if (!oaiClaimId || !tenantId) {
    const fallbackTenant = getTntIdForNavigation()
    navigate(fallbackTenant ? `/${fallbackTenant}/claims` : '/claims')
    return <></>
  }

  // If manual tenant switch is in progress, show loading state instead of trying to fetch claim
  // This prevents 404 errors during navigation
  if (isManualTenantSwitch()) {
    return <LoadingBox />
  }

  // If we're retrieving the correct tenant, show loading state instead of error
  if (isRetrievingTenant) {
    return <LoadingBox />
  }

  /**
   * Services
   */

  const { data: claim, error: claimError, isLoading: isLoadingClaim } = useClaim(oaiClaimId)

  // Handle claim errors - try to retrieve correct tenant first before redirecting
  useEffect(() => {
    if (claimError && oaiClaimId && !hasTriedTenantRetrieval.current && profile) {
      sendLogs({ message: `ClaimDetailsPage.tsx ~ claim error: ${claimError}` })

      // Mark that we've tried tenant retrieval to prevent infinite loops
      hasTriedTenantRetrieval.current = true

      // Skip automatic tenant switching if this is a manual tenant switch
      // Manual switches handle navigation in TenantSelection component
      if (isManualTenantSwitch()) {
        return
      }

      // Show loading state while retrieving tenant
      setIsRetrievingTenant(true)

      // Try to retrieve the correct tenant for this claim (automatic switching)
      // If successful, retrieveTenantId will reload the page with correct tenant
      retrieveTenantId({
        claimId: oaiClaimId,
        profile,
        onError: () => {
          // If retrieveTenantId fails (different customer or no access),
          // the error message is already set in sessionStorage by retrieveTenantId
          // Use window.location.href to ensure the page reloads and shows the notification
          window.location.href = getClaimsPath(tenantId)
        }
      })
    }
  }, [claimError, oaiClaimId, tenantId, navigate, profile])

  const { data: entities, isLoading: isLoadingEntities } = useEntities({
    onError: (error: AxiosError) => errorHandler.handleError(error, ErrorMessagesEnum.EntitiesError),
  })
  const { data: currentUserLiveCallsPaginatedResponse } = useCurrentUserCalls(user?.id)

  /**
   * Variables
   */
  const claimOwner = claim?.owner
  const claimOwnerId = claim?.ownerId
  const isOwner = isCurrentUserClaimOwner(claimOwnerId, user!)
  const callInProgress = currentUserLiveCallsPaginatedResponse?.data.find(
    (call) => call.oaiClaimId === claim?.oaiClaimId,
  )
  const isCallInProgress = !!callInProgress

  const [showCallStartedModal, setShowCallStartedModal] = useState<null | { type: CallTypeEnum; callId: string }>(null)
  const [startingCall, setStartingCall] = useState<null | {
    callId: string
    type: CallTypeEnum
  }>(null)
  const { data: call } = useCall(startingCall?.callId, {
    refreshInterval: RefreshIntervalsEnum.useCallForTalkToPayorRepCalls,
  })

  useEffect(() => {
    if (entities && claim) {
      const mappedSlotData = mapEntities((entities as any) || [], claim || {})
      setSlotData(mappedSlotData)
    }
  }, [entities, claim])

  useEffect(() => {
    if (startingCall && call) {
      if (call?.status === CallStatusEnum.InProgress) {
        setShowCallStartedModal(startingCall)
        setStartingCall(null)
      }

      if (call?.status === CallStatusEnum.FailedToQueue) {
        notificator.notify({ heading: 'Call failed to queue, please report this to the administrator', type: 'error' })
        //notificator.error('Call failed to queue, please report this to the administrator')
      }
      if (call?.status === CallStatusEnum.FailedToComplete) {
        notificator.notify({
          heading: 'Call failed to complete, please report this to the administrator',
          type: 'error',
        })
        //notificator.error('Call failed to complete, please report this to the administrator')
      }
    }
  }, [startingCall, call])

  useEffect(() => {
    if (!claim || !oaiClaimId) return

    if (claim.claimId && oaiClaimId === claim.oaiClaimId && claim.claimId !== oaiClaimId) {
      const parts = window.location.pathname.split('/')
      const claimIndex = parts.lastIndexOf('claim')
      if (claimIndex !== -1 && parts[claimIndex + 1] === oaiClaimId) {
        parts[claimIndex + 1] = claim.claimId
        const newPath = parts.join('/')

        navigate(newPath, { replace: true })
      }
    }
  }, [claim, oaiClaimId, navigate])

  const [showStandardCall, setShowStandardCall] = useState(false)
  const title = claim?.visitId ? `${claim.visitId}` : 'Claims'
  const { mutate: mutateClaim } = useClaim(oaiClaimId)

  const { data: userPhonePreference } = useUserPreference(UserPreferencesKeysEnum.softphonePhoneNumbers)
  localStorage.setItem('levelOfSupport', JSON.stringify(useLevelofSupport().data))

  const userPhoneData = JSON.parse(userPhonePreference?.value || '{}')
  const isCallWithTakeOverDisabled =
    ['denied', 'deniedByOS'].includes(microphonePermissionStatus) &&
    (!userPhoneData?.currentSelection || userPhoneData.currentSelection === DEFAULT_PAYER_VA_SOFTPHONE_KEY)
  const isDialCallDisabled: boolean = (activeCallJobId ? true : false) || (!claim?.levelOfSupport ? true : false)
  const rawPhoneNumber =
    mapEntities((entities as any) || [], claim || {}, { slot: 'insurerPhoneNumber', onlyValue: true }) ||
    claim?.insurerPhoneNumber ||
    ''
  const phoneNumber = formatPhoneNumber(rawPhoneNumber)

  /**
   *
   * redirectToLiveCalls
   */
  const redirectToLiveCalls = (activeCallJobId: string) => {
    navigate(`${PathsEnum.call}/${activeCallJobId}`)
  }

  const redirectToClaims = () => {
    navigate(getClaimsPath(tenantId))
  }

  const checkPayerAvailability = () => {
    if (isCallWithTakeOverDisabled) {
      notificator.notify({
        heading: 'Microphone access is disabled',
        message: (
          <>
            In order to start assisted calls, you will need to enable microphone access in your browser. To learn more,{' '}
            <a
              href={getUnblockMicHelperUrl(getBrowser())}
              className="text-decoration-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>
          </>
        ),
        type: 'warn',
        toastOptions: { hideProgressBar: true, toastId: 'micBlockedNotification' },
      })
      return
    }

    if (!claim?.insurerPhoneNumber) {
      sendLogs({ message: 'ClaimCallMenu.tsx ~ checkPayerAvailability ~ insurerPhoneNumber:undefined' })
      return
    }
    const query = apiPaths.phoneNumber(`${claim?.insurerPhoneNumber}/isActive`)

    getPayerAvailability(query)
      .then(({ isActive }) => {
        if (isActive) {
          setShowStandardCall(true)
        } else {
          setShowPayerCallRequestModal(true)
        }
      })
      .catch((error: any) => {
        const message = error.response.data.detail
        const { status } = error.response

        if (status === 404) {
          setShowStandardCall(true)
          return
        }
        notificator.notify({ heading: message, type: 'error' })
        //notificator.error(message)
      })
  }

  const handleUserResponsePayerRequest = (callRequest: boolean) => {
    setShowPayerCallRequestModal(callRequest)
    if (callRequest) {
      setShowPayerCallRequestModal(false)
      setShowStandardCall(true)
    }
  }

  const handleStartCall = (type: CallTypeEnum, useCase: UseCaseEnum) => async () => {
    try {
      if (!claim) return
      const jobResponse: ICall = await startCall(claim.oaiClaimId, { type, useCase })

      if (notificator.isDesktopNotificationsNotSelected()) notificator.getDesktopNotificationPermission()

      //TODO When we will focus on performance we will create a global mutator that receives an array of desired mutations and we will mutate only locally
      await revalidateAllSwrCache()
      if ([CallTypeEnum.attendable, CallTypeEnum.humanAgent].includes(type)) {
        dispatch(
          updateLiveCall({
            activeCallJobId: jobResponse.jobId,
            type,
            activeClaimId: claim?.oaiClaimId,
            claimId: claim?.claimId,
            activeCallJbId: jobResponse.jbId,
          }),
        )
      } else if ([CallTypeEnum.standard, CallTypeEnum.diagnosticHeadset].includes(type)) {
        dispatch(
          updateLiveCall({
            activeCallJobId: jobResponse.jobId,
            type,
            activeClaimId: claim?.oaiClaimId,
            automationType: AutomationTypeEnum.basic,
            claimId: claim?.claimId,
            activeCallJbId: jobResponse.jbId,
          }),
        )
        redirectToLiveCalls(jobResponse.jobId)
      }
      setStartingCall({ type, callId: jobResponse.jobId })
    } catch (error: any) {
      errorHandler.handleError(error, ErrorMessagesEnum.StartCall)
    }
  }

  const claimsArchive = (status: boolean) => {
    const claimArchive = [
      {
        oaiClaimId: claim.oaiClaimId,
        archived: status,
      },
    ]
    archiveClaim({ claims: claimArchive })
      .then((response) => {
        if (response) {
          notificator.notify({
            heading: `This claim has been successfully ${status ? 'archived' : 'unarchived'}`,
            type: 'success',
          })
          //notificator.success(`This claim has been successfully ${status ? 'archived' : 'unarchived'}`)
        }
        revalidateAllSwrCache()
        mutateClaim('', true)
      })
      .catch((error) => {
        errorHandler.handleError(error.message)
      })
  }

  const isLoading = isLoadingClaim || isLoadingEntities
  const error = claimError
  const hasClaimReady = !!claim && !isLoading && !error

  if (error) {
    return <ErrorBox image={<NoResultsAfterFilters />} {...errorHandler.errorToErrorBoxProps(error)} />
  }
  return (
    <>
      <Div>
        {isLoading && <LoadingBox message="Fetching Claim Details..." />}

        {hasClaimReady && (
          <>
            <BreadcrumbSectionContainerDiv isOwner={isOwner} className="access-permission">
              <BreadcrumbHeader
                title={title}
                breadcrumbs={[{ title: 'Claims', path: getClaimsPath(tenantId) }, { title: 'Claim Details' }]}
                showBackButton={true}
                backAction={() => redirectToClaims()}
                navComponent={
                  <Breadcrumb>
                    <Breadcrumb.Item onClick={() => redirectToClaims()}>Claims</Breadcrumb.Item>
                    <Breadcrumb.Item active>Claim Details</Breadcrumb.Item>
                  </Breadcrumb>
                }
                suppressTooltip={true}
                isCallInProgress={callInProgress}
              >
                <section className="call-disposition-container" data-testid="call-disposition-container">
                  <div className="claimStatusContainer">
                    {isOwner && (
                      <div className="archive-holder">
                        <Button
                          variant="flat"
                          radius="normal"
                          contentType="text-icon"
                          size="normal"
                          onClick={() => claimsArchive(!claim.archived)}
                        >
                          {claim.archived ? (
                            <>
                              <FaRegCheckSquare /> Unarchive
                            </>
                          ) : (
                            <>
                              <FaCheckSquare /> Archive
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {isOwner ? (
                    <div className="claimCallContainer">
                      {isCallInProgress ? (
                        <LiveCallActionDisplay oaiClaimId={oaiClaimId} callInProgress={callInProgress} />
                      ) : (
                        <div className="d-flex align-items-center gap-2">
                          {/* Temprary removed until product decides on how to implement the feature */}
                          {/* <div className="p-relative">
                            <SelectTakeOverModeDropdown />
                          </div> */}
                          <StartManualCallButton
                            isDialCallDisabled={isDialCallDisabled || claim.archived || !assistedCallEnabled}
                            setShowStandardCall={() => checkPayerAvailability()}
                          />
                          <ClaimCallJobStandardModal
                            show={showStandardCall}
                            setShow={setShowStandardCall}
                            phoneNumber={phoneNumber}
                            startCall={handleStartCall(CallTypeEnum.standard, UseCaseEnum.CSI)}
                            claim={claim}
                          />

                          {showCallStartedModal && (
                            <ClaimCallJobStartedModal
                              claimId={claim.oaiClaimId}
                              type={showCallStartedModal.type}
                              callId={showCallStartedModal.callId}
                            />
                          )}
                          <ClaimCallMenu
                            disabled={claim.archived}
                            claim={claim}
                            activeCallJobId={activeCallJobId}
                            phoneNumber={phoneNumber}
                          />
                        </div>
                      )}
                      {/* <div className="owner-name">
                        <ClaimOwnerAvatar claimOwner={claimOwner} />
                        <span>{claimOwner}</span>
                      </div> */}
                    </div>
                  ) : (
                    ''
                    // <NoAccessNoOwner callInProgress={callInProgress} claim={claim} entityBag={entityBag} />
                  )}

                  <AssignAndUnAssign
                    claimOwner={claimOwner}
                    claimOwnerId={claimOwnerId}
                    visitId={claim?.visitId}
                    oaiClaimId={claim?.oaiClaimId}
                  />
                </section>
              </BreadcrumbHeader>
            </BreadcrumbSectionContainerDiv>

            <div className="background-textured">{<ClaimDetails claim={claim} slotData={slotData || []} />}</div>

            {showPayerCallRequestModal && (
              <PayerCallRequestConfirmationModal
                callPayer={(val: boolean) => handleUserResponsePayerRequest(val)}
                show={showPayerCallRequestModal}
              />
            )}
          </>
        )}
      </Div>
    </>
  )
}

const reactSvgComponentToMarkupString = (Component: any, props: any) =>
  `data:image/svg+xml,${encodeURIComponent(renderToStaticMarkup(createElement(Component, props)))}`

const BreadcrumbSectionContainerDiv = styled.div<{ isOwner: boolean }>`
  position: relative;
  z-index: 9;
  background: ${({ theme }) => theme.colors.neutral.lighter};

  .ivr-call-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    ${({ theme }) => theme.typography.s.normal};
    line-height: 150%;
    padding-right: 1.5rem;
    color: ${({ theme }) => theme.colors.primary.darker};
  }

  .icon {
    width: 10px;
    height: 10px;
    color: #b01f1f;
  }
`

const Div = styled.div`
  .left-section-breadcrumbs {
    margin-top: 0.2rem;
  }

  .breadcrumb {
    position: relative;
    top: 10px;
    li {
      a {
        ${({ theme }) => theme.typography.xs.medium};
        color: ${({ theme }) => theme.colors.info.neutral};
        cursor: pointer;
        text-decoration: none !important;
      }
    }
    .breadcrumb-item a {
      color: ${({ theme }) => theme.colors.info.neutral};
      text-decoration: none;
    }

    .breadcrumb-item.active {
      ${({ theme }) => theme.typography.xs.medium};
      color: ${({ theme }) => theme.colors.grey.dark};
    }
    .breadcrumb-item + .breadcrumb-item::before {
      content: ${({ theme }) =>
    `url(${reactSvgComponentToMarkupString(FaCaretRight, {
      color: theme.colors.grey.neutral,
    })})`}!important;
      margin-top: 2px;
    }
  }

  .label {
    color: ${({ theme }) => theme.colors.grey.light};
  }

  .info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    ${({ theme }) => theme.typography.s.normal};
  }

  .background-textured {
    /* height: calc(100vh - ${({ theme }) => theme.constants.navbarwithBreadcrumbsHeight}); */
    height: calc(100vh - 10rem);
    position: relative;
    overflow: auto;
    padding: ${({ theme }) => theme.spacing.m};
  }

  .archive-holder {
    /* margin-left: 1rem; */
    margin-right: 0.5rem;
  }

  .call-disposition-container {
    display: flex;
    justify-content: space-between;
    /* flex: 1; */
  }
  .call-btn {
    margin-right: 0.5rem;
    ${({ theme }) => theme.typography.m.medium};
    color: ${({ theme }) => theme.colors.grey.lightest};
  }
  .call-dropdown-option .tooltip-container {
    left: ${({ theme }) => theme.util.pixelToRem(-166)};
    top: ${({ theme }) => theme.util.pixelToRem(22)};
    height: fit-content;
  }
  .call-dropdown-option .tooltip-arrow {
    top: 20px;
    border-color: transparent transparent transparent ${({ theme }) => theme.colors.grey.lighter} !important;
  }
  .call-dropdown-option .tooltip-arrow::after {
    border-color: transparent transparent transparent ${({ theme }) => theme.colors.grey.lightest} !important;
  }
  .call-dropdown-option button {
    height: 2.5rem;
    display: flex;
    align-items: center;
    border: none;
    font-weight: 600;
    svg {
      ${({ theme }) => theme.typography.m.medium};
    }
  }
  .call-dropdown-option button.btn-primary:focus {
    box-shadow: none;
    border: none;
  }

  .claimCallContainer .tooltip-arrow {
    border-color: transparent transparent ${({ theme }) => theme.colors.grey.lighter} transparent;
  }
  .claimCallContainer .tooltip-arrow::after {
    border-color: transparent transparent ${({ theme }) => theme.colors.grey.lightest} transparent;
  }

  .lock-icon {
    color: #8d9199;
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.125rem;
    margin-top: 0.3125rem;
  }
  .claimStatusContainer {
    display: flex;
    align-items: end;
    /* margin-left: 1rem; */
  }

  .claimCallContainer {
    display: flex;
    align-items: center;

    .take-ownership-action {
      font-size: 16px;
      font-weight: 500;
      color: ${({ theme }) => theme.colors.info.darker};
    }
  }
  /* .claimCallContainer .tooltip-container {
    background: #ffffff;
    border: 1px solid #d9dadd;
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: #002366;
  } */
  /* .dropdown-menu.show {
    z-index: 7;
    padding: 0;
    background-color: ${({ theme }) => theme.colors.neutral.lightest};
    border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    margin-top: 5px;
    padding: ${({ theme }) => theme.spacing.xs} 0;
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
  }
  .dropdown-submenu {
    width: 14.8125rem;
    text-align: left;
    box-shadow: 0 4px 4px 0 rgb(0 0 0 / 10%);
    border: solid 1px #e5e9f0;
  }
  .dropdown-item:hover {
    background-color: ${({ theme }) => theme.colors.grey.lighter} !important;
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
  .dropdown-menu .dropdown-item.active {
    background-color: ${({ theme }) => theme.colors.grey.lighter} !important;
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
  .dropdown-item {
    ${({ theme }) => theme.typography.s.normal};
    padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.s};
  }
  .dropdown-item:disabled {
    color: ${({ theme }) => theme.colors.grey.light}!important;
    cusor: not-allowed;
  } */
  .dropdown-divider {
    margin-top: ${({ theme }) => theme.spacing.xxs};
    margin-bottom: ${({ theme }) => theme.spacing.xxs};
    display: flex;
    border-top: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  }

  .non-owner-name .tooltip-children-container {
    display: flex;
    align-items: center;
  }
  .non-owner-name .tooltip-children-container span {
    margin-left: 11px;
  }
  .non-owner-name,
  .owner-name {
    display: flex;
    align-items: center;
    padding: 0 0.5rem;
    margin-right: 1.5rem;
    height: 2rem;
    border-radius: 9px;
    background: ${({ theme }) => theme.colors.neutral.light};
  }
  /* .btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.xxs};
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    outline: none;
    border: none;
    background-color: ${({ theme }) => theme.colors.info.neutral} !important;
    ${({ theme }) => theme.typography.s.medium};
    border-radius: ${({ theme }) => theme.borderRadius.rounded};
    color: ${({ theme }) => theme.colors.neutral.lightest};
    padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
  }
  .show > .btn-primary.dropdown-toggle {
    background-color: ${({ theme }) => theme.colors.info.darker} !important;
    border: transparent !important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
  }
  .btn-primary:click {
    border: none !important;
    background-color: ${({ theme }) => theme.colors.info.dark}!important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
  }
  .btn-primary:focus {
    border: none !important;
    background-color: ${({ theme }) => theme.colors.info.dark}!important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
    box-shadow: none !important;
  }
  .btn-primary:hover {
    border: transparent !important;
    background-color: ${({ theme }) => theme.colors.info.dark}!important;
    color: ${({ theme }) => theme.colors.info.lightest} !important;
  }
  .btn-primary::before {
    display: none;
  } */
  .non-owner-name,
  .owner-name span {
    margin-left: 0.625rem;
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.14;
    color: ${({ theme }) => theme.colors.grey.dark};
  }
`
